import React, { useEffect, useState } from 'react';
import { accountService } from '../../../services/api';

interface CriticalBreachModalProps {
    isOpen: boolean;
    onClose: () => void;
    missingTypes: string[];
    propertyId?: string;
}

export const CriticalBreachModal: React.FC<CriticalBreachModalProps> = ({ isOpen, onClose, missingTypes, propertyId }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [authorizedBy, setAuthorizedBy] = useState('');

    const documentTypeLabels: Record<string, string> = {
        'gas_safety': 'Gas Safety (CP12)',
        'epc': 'EPC (Energy Performance Certificate)',
        'eicr': 'EICR (Electrical Safety)',
        'electrical': 'EICR (Electrical Safety)',
        'energy': 'EPC (Energy Performance)',
        'gas': 'Gas Safety (CP12)'
    };

    const formatLabel = (type: string) => documentTypeLabels[type.toLowerCase()] || type.replace(/_/g, ' ').toUpperCase();

    // Allow closing with Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleAcknowledge = async () => {
        setIsSubmitting(true);
        try {
            await accountService.logAuditEvent({
                action: 'CRITICAL_BREACH_ACKNOWLEDGED',
                severity: 'critical',
                details: {
                    propertyId,
                    missingDocuments: missingTypes,
                    authorizedBy: authorizedBy || 'Self-Acknowledge',
                    acknowledgedAt: new Date().toISOString()
                }
            });
            onClose();
        } catch (error) {
            console.error('[CriticalBreachModal] Failed to log audit event:', error);
            // Non-blocking for now if API fails (403 hotfix)
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-900 opacity-95"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border-4 border-red-600">
                    <div className="bg-red-600 px-6 py-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <i className="fas fa-exclamation-triangle text-white text-2xl animate-pulse"></i>
                            <h3 className="text-2xl leading-7 font-black text-white uppercase tracking-tight" id="modal-title">
                                Compliance Notice
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors"
                            aria-label="Close"
                        >
                            <i className="fas fa-times text-xl"></i>
                        </button>
                    </div>

                    <div className="bg-white px-6 pt-6 pb-6 sm:p-8 sm:pb-6">
                        <div className="sm:flex sm:items-start">
                            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                <p className="text-base text-gray-900 font-black mb-6 border-b pb-2 uppercase tracking-wide">
                                    The following mandatory legal documents are missing or expired:
                                </p>
                                <div className="space-y-3 mb-8">
                                    {missingTypes.map((type, idx) => (
                                        <div key={idx} className="flex items-center text-lg text-red-700 bg-red-50 border border-red-200 p-4 rounded-xl font-black shadow-sm">
                                            <i className="fas fa-times-circle mr-3 text-red-600"></i>
                                            {formatLabel(type)}
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-slate-900 p-5 rounded-xl border border-slate-700 mb-8 shadow-inner">
                                    <p className="text-sm text-slate-200 leading-relaxed font-medium">
                                        Operating a tenancy without these documents is a **criminal offense** under UK Housing Law.
                                        You are liable for heavy fines, civil penalties, and the absolute inability to serve valid eviction notices.
                                    </p>
                                </div>

                                {/* New Reference Field */}
                                <div className="mb-6">
                                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Reference / Authorized By</label>
                                    <input
                                        type="text"
                                        placeholder="Admin Name or Authorization Ref"
                                        value={authorizedBy}
                                        onChange={(e) => setAuthorizedBy(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 outline-none transition-all font-bold"
                                    />
                                </div>

                                <div className="flex items-start gap-4 p-4 bg-red-50 rounded-xl border border-red-100">
                                    <input
                                        type="checkbox"
                                        id="ack_check"
                                        checked={isChecked}
                                        onChange={(e) => setIsChecked(e.target.checked)}
                                        className="mt-1 h-6 w-6 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer shadow-sm"
                                    />
                                    <label htmlFor="ack_check" className="text-sm font-black text-red-900 cursor-pointer leading-tight">
                                        I explicitly acknowledge this critical breach and understand all legal and criminal risks of continuing operations.
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-6 sm:px-8 sm:flex sm:flex-row-reverse border-t border-gray-100">
                        <button
                            type="button"
                            disabled={!isChecked || isSubmitting}
                            className={`w-full inline-flex justify-center rounded-xl border border-transparent shadow-xl px-8 py-4 text-lg font-black text-white transition-all sm:ml-3 sm:w-auto ${!isChecked || isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700 active:scale-95 shadow-red-200'
                                }`}
                            onClick={handleAcknowledge}
                        >
                            {isSubmitting ? (
                                <><i className="fas fa-spinner fa-spin mr-2"></i> RECORDING ACKNOWLEDGMENT...</>
                            ) : (
                                'I CONFIRM & ACKNOWLEDGE THIS BREACH'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
